import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Helpers
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const SAC_PER_30S_BLOCK = 9;
const MAX_BLOCKS_PER_24H_PER_USER = 240;
const MAX_CAMPAIGNS_PER_USER = 100;

const blocksFromSac = (sac: number) => {
  const blocks = sac / SAC_PER_30S_BLOCK;
  return Number.isFinite(blocks) ? Math.round(blocks) : 0;
};

const sacFromBlocks = (blocks: number) => {
  const safeBlocks = Number.isFinite(blocks) ? Math.max(0, Math.floor(blocks)) : 0;
  return safeBlocks * SAC_PER_30S_BLOCK;
};

const coerceUuid = (val: any): string | null => {
  if (!val) return null;
  if (typeof val === 'string' && UUID_RE.test(val)) return val;
  if (typeof val === 'object') {
    if (val.id && typeof val.id === 'string' && UUID_RE.test(val.id)) return val.id;
    if (val.ad_account_id && typeof val.ad_account_id === 'string' && UUID_RE.test(val.ad_account_id)) return val.ad_account_id;
  }
  return null;
};

// Pick only valid ad_creatives columns for update (avoid accidental JSON into uuid fields)
const buildCreativeUpdate = (updates: any) => {
  const allowed: any = {};
  const keys = [
    'name',
    'type',
    'title',
    'body',
    'call_to_action',
    'destination_url',
    'display_url',
    'media_urls',
    'thumbnail_url',
    'video_duration',
    'aspect_ratio',
    'is_active',
  ];
  for (const k of keys) {
    if (Object.prototype.hasOwnProperty.call(updates, k)) {
      // Basic normalization for media_urls
      if (k === 'media_urls') {
        const v = updates[k];
        try {
          allowed[k] = typeof v === 'string' ? JSON.parse(v) : v;
        } catch {
          allowed[k] = v; // let server validate
        }
      } else {
        allowed[k] = updates[k];
      }
    }
  }
  // Never allow changing ad_account_id here (prevents uuid cast errors)
  allowed.updated_at = new Date().toISOString();
  return allowed;
};

// File upload function for creatives
export const uploadCreativeFile = async (file: File): Promise<string> => {
  const fileExt = file.name.split('.').pop();
  const fileName = `creatives/${Date.now()}.${fileExt}`;

  const { error: uploadError } = await supabase.storage
    .from('ads-media')
    .upload(fileName, file);

  if (uploadError) throw uploadError;

  const { data } = supabase.storage
    .from('ads-media')
    .getPublicUrl(fileName);

  return data.publicUrl;
};

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

class AdsApiService {
  private setupUserAccountPromise: Promise<any> | null = null;

  // Health check
  async healthCheck() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      return {
        success: true,
        message: 'SoArtistic Ads API is running',
        version: '1.0.0',
        user: user?.id || null
      };
    } catch (error) {
      throw new Error('API connection failed');
    }
  }

  // Ad Accounts
  async getAdAccounts() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('ad_accounts')
      .select(`
        *,
        sac_balance
      `)
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async createAdAccount(accountData: any) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('ad_accounts')
      .insert({
        user_id: user.id,
        account_name: accountData.account_name,
        account_type: accountData.account_type || 'personal',
        business_name: accountData.business_name,
        sac_balance: 0, // Initial SAC balance
      })
      .select()
      .single();

    if (error) throw error;
    console.log('✅ Created ad account:', data);
    return data;
  }

  async updateAdAccount(accountId: string, updates: any) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('ad_accounts')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', accountId)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Creatives
  async getCreatives(filters: { search?: string; startDate?: string } = {}) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // First, get all ad_account_ids for the current user
    const { data: adAccounts, error: adAccountsError } = await supabase
      .from('ad_accounts')
      .select('id')
      .eq('user_id', user.id);

    if (adAccountsError) throw adAccountsError;

    const adAccountIds = adAccounts.map(account => account.id);

    if (adAccountIds.length === 0) {
      return []; // No ad accounts for this user, so no creatives
    }

    let query = supabase
      .from('ad_creatives')
      .select(`*`) // Select all columns from ad_creatives
      .in('ad_account_id', adAccountIds); // Filter by the user's ad account IDs

    if (filters.search) {
      query = query.ilike('name', `%${filters.search}%`);
    }
    if (filters.startDate) {
      query = query.gte('created_at', filters.startDate);
    }

    query = query.order('created_at', { ascending: false });

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  }

  async updateCreative(creativeId: string, updates: any) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Verify ownership by checking the user's ad_account_ids and matching the creative's ad_account_id
    const { data: adAccounts, error: adAccountsError } = await supabase
      .from('ad_accounts')
      .select('id')
      .eq('user_id', user.id);

    if (adAccountsError) throw adAccountsError;

    const adAccountIds = (adAccounts || []).map((a: any) => a.id);
    if (adAccountIds.length === 0) {
      throw new Error('No ad accounts found for this user.');
    }

    const { data: existingCreative, error: creativeFetchError } = await supabase
      .from('ad_creatives')
      .select('id, ad_account_id')
      .eq('id', creativeId)
      .in('ad_account_id', adAccountIds)
      .single();

    if (creativeFetchError || !existingCreative) {
      throw new Error('Creative not found or you do not have permission to update it.');
    }

    const safeUpdates = buildCreativeUpdate(updates);

    const { data, error } = await supabase
      .from('ad_creatives')
      .update(safeUpdates)
      .eq('id', creativeId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async deleteCreative(creativeId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Verify ownership by checking the user's ad_account_ids and matching the creative's ad_account_id
    const { data: adAccounts, error: adAccountsError } = await supabase
      .from('ad_accounts')
      .select('id')
      .eq('user_id', user.id);

    if (adAccountsError) throw adAccountsError;

    const adAccountIds = (adAccounts || []).map((a: any) => a.id);
    if (adAccountIds.length === 0) {
      throw new Error('No ad accounts found for this user.');
    }

    const { data: creative, error: creativeFetchError } = await supabase
      .from('ad_creatives')
      .select('id, ad_account_id')
      .eq('id', creativeId)
      .in('ad_account_id', adAccountIds)
      .single();

    if (creativeFetchError || !creative) {
      throw new Error('Creative not found or you do not have permission');
    }

    const { error } = await supabase
      .from('ad_creatives')
      .delete()
      .eq('id', creativeId);

    if (error) throw error;
    return true;
  }

  async createCreative(creativeData: any) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Verify ad account belongs to user
    const accountId = coerceUuid(creativeData.ad_account_id);
    if (!accountId) {
      throw new Error('Invalid ad_account_id provided');
    }

    const { data: account, error: accountError } = await supabase
      .from('ad_accounts')
      .select('id')
      .eq('id', accountId)
      .eq('user_id', user.id)
      .single();

    if (accountError || !account) {
      throw new Error('Ad account not found or you do not have permission');
    }

    const { data, error } = await supabase
      .from('ad_creatives')
      .insert({
        ad_account_id: accountId,
        name: creativeData.name,
        type: creativeData.type,
        title: creativeData.title,
        body: creativeData.body,
        call_to_action: creativeData.call_to_action || 'learn_more',
        destination_url: creativeData.destination_url,
        display_url: creativeData.display_url,
        media_urls: creativeData.media_urls || [],
        thumbnail_url: creativeData.thumbnail_url,
        aspect_ratio: creativeData.aspect_ratio || '1:1',
        is_active: true
      })
      .select()
      .single();

    if (error) throw error;
    console.log('✅ Created creative:', data);
    return data;
  }

  // Campaigns
  async getCampaigns(accountId?: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Get the user's ad account IDs
    const { data: adAccounts, error: adAccountsError } = await supabase
      .from('ad_accounts')
      .select('id')
      .eq('user_id', user.id);

    if (adAccountsError) throw adAccountsError;
    const adAccountIds = (adAccounts || []).map((a: any) => a.id);
    if (adAccountIds.length === 0) return [];

    let query = supabase
      .from('ad_campaigns')
      .select(`
        *,
        ad_sets(
          id,
          name,
          status,
          budget_amount,
          ads(
            id, 
            name, 
            status, 
            review_status,
            ad_creatives(
              id,
              name,
              title,
              type,
              thumbnail_url
            )
          )
        )
      `)
      .in('ad_account_id', adAccountIds);

    if (accountId) {
      // Ensure the provided accountId belongs to the user
      if (!adAccountIds.includes(accountId)) {
        return [];
      }
      query = query.eq('ad_account_id', accountId);
    }

    query = query.order('created_at', { ascending: false });

    const { data, error } = await query;
    if (error) throw error;
    
    console.log('✅ Fetched campaigns with full structure:', data);
    return data || [];
  }

  async createCampaign(campaignData: any) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    console.log('🚀 Creating complete campaign structure:', campaignData);

    const desiredBlocks = blocksFromSac(campaignData.budget_amount);
    if (desiredBlocks <= 0) {
      throw new Error('El presupuesto debe ser al menos 1 bloque de 30 segundos.');
    }
    if (desiredBlocks > MAX_BLOCKS_PER_24H_PER_USER) {
      throw new Error(`El máximo por usuario es ${MAX_BLOCKS_PER_24H_PER_USER} bloques cada 24 horas (2 horas).`);
    }
    const normalizedBudgetAmount = sacFromBlocks(desiredBlocks);

    // Verify ad account belongs to user and get SAC balance
    const { data: account, error: accountError } = await supabase
      .from('ad_accounts')
      .select('id, sac_balance')
      .eq('id', campaignData.ad_account_id)
      .eq('user_id', user.id)
      .single();

    if (accountError || !account) {
      throw new Error('Ad account not found or you do not have permission');
    }

    const { data: adAccounts, error: adAccountsError } = await supabase
      .from('ad_accounts')
      .select('id')
      .eq('user_id', user.id);
    if (adAccountsError) throw adAccountsError;

    const adAccountIds = (adAccounts || []).map((a: any) => a.id);
    const { count: campaignCount, error: countError } = await supabase
      .from('ad_campaigns')
      .select('id', { count: 'exact', head: true })
      .in('ad_account_id', adAccountIds);
    if (countError) throw countError;
    if ((campaignCount || 0) >= MAX_CAMPAIGNS_PER_USER) {
      throw new Error(`Has alcanzado el límite de ${MAX_CAMPAIGNS_PER_USER} campañas por usuario.`);
    }

    const { data: activeCampaigns, error: activeError } = await supabase
      .from('ad_campaigns')
      .select('id, budget_amount')
      .in('ad_account_id', adAccountIds)
      .eq('status', 'active');
    if (activeError) throw activeError;
    const activeBlocks = (activeCampaigns || []).reduce((sum: number, c: any) => sum + blocksFromSac(c.budget_amount || 0), 0);
    if (activeBlocks + desiredBlocks > MAX_BLOCKS_PER_24H_PER_USER) {
      throw new Error(`Superas el máximo de 2 horas por usuario cada 24h. Te quedan ${Math.max(0, MAX_BLOCKS_PER_24H_PER_USER - activeBlocks)} bloques disponibles.`);
    }

    // Check SAC balance availability
    if (account.sac_balance < normalizedBudgetAmount) {
      throw new Error('No tienes suficiente saldo de SoArtistic Coins (SAC) para este consumo. Compra más SAC o reduce los bloques.');
    }

    // 1. Create campaign
    const { data: campaign, error: campaignError } = await supabase
      .from('ad_campaigns')
      .insert({
        ad_account_id: campaignData.ad_account_id,
        name: campaignData.name,
        objective: campaignData.objective,
        budget_type: campaignData.budget_type || 'daily',
        budget_amount: normalizedBudgetAmount,
        start_date: campaignData.start_date || new Date().toISOString(),
        end_date: campaignData.end_date || null,
        bid_strategy: campaignData.bid_strategy || 'lowest_cost',
        status: 'paused' // Start paused, will activate after setup
      })
      .select()
      .single();

    if (campaignError) throw campaignError;
    console.log('✅ 1. Created campaign:', campaign);

    // 2. Create default ad set
    const { data: adSet, error: adSetError } = await supabase
      .from('ad_sets')
      .insert({
        campaign_id: campaign.id,
        name: `${campaignData.name} - Conjunto Principal`,
        status: 'paused',
        budget_amount: normalizedBudgetAmount,
        optimization_goal: 'link_clicks',
        billing_event: 'impressions',
        start_date: campaignData.start_date || new Date().toISOString(),
        end_date: campaignData.end_date || null
      })
      .select()
      .single();

    if (adSetError) throw adSetError;
    console.log('✅ 2. Created ad set:', adSet);

    // 3. Create targeting for ad set
    const { error: targetingError } = await supabase
      .from('ad_targeting')
      .insert({
        ad_set_id: adSet.id,
        age_min: 18,
        age_max: 65,
        genders: ['all'],
        locations: [],
        languages: ['es'],
        interests: [],
        behaviors: [],
        device_platforms: ['mobile', 'desktop'],
        placements: ['feed', 'stories']
      });

    if (targetingError) throw targetingError;
    console.log('✅ 3. Created targeting');

    // 4. Create ads for selected creatives
    const createdAds = [];
    if (campaignData.selected_creatives && campaignData.selected_creatives.length > 0) {
      console.log('📎 Creating ads for', campaignData.selected_creatives.length, 'creatives');
      
      for (const creativeId of campaignData.selected_creatives) {
        try {
          // Get creative details for ad name
          const { data: creative } = await supabase
            .from('ad_creatives')
            .select('name, title')
            .eq('id', creativeId)
            .single();

          const { data: ad, error: adError } = await supabase
            .from('ads')
            .insert({
              ad_set_id: adSet.id,
              creative_id: creativeId,
              name: `${creative?.title || creative?.name || 'Ad'} - ${campaign.name}`,
              status: 'active',
              review_status: 'approved' // Auto-approve for SoArtistic
            })
            .select(`
              *,
              ad_creatives(
                id,
                name,
                title,
                type,
                thumbnail_url
              )
            `)
            .single();

          if (adError) {
            console.error('❌ Error creating ad for creative', creativeId, ':', adError);
          } else {
            createdAds.push(ad);
            console.log('✅ Created ad for creative:', creativeId);
          }
        } catch (err) {
          console.error('❌ Unexpected error creating ad:', err);
        }
      }
    }

    console.log('✅ 4. Created', createdAds.length, 'ads');

    // 5. Activate campaign and ad set if ads were created successfully
    if (createdAds.length > 0) {
      // Activate ad set
      await supabase
        .from('ad_sets')
        .update({ status: 'active' })
        .eq('id', adSet.id);

      // Activate campaign
      await supabase
        .from('ad_campaigns')
        .update({ status: 'active' })
        .eq('id', campaign.id);

      console.log('✅ 5. Activated campaign and ad set');

      // Create initial budget record (now in SAC)
      await supabase
        .from('ad_budgets')
        .insert({
          ad_account_id: campaignData.ad_account_id,
          campaign_id: campaign.id,
          budget_type: 'campaign',
          allocated_amount: normalizedBudgetAmount,
        });

      console.log('✅ 6. Created budget record');

    } else {
      console.warn('⚠️ No ads created, campaign remains paused');
    }

    // Return campaign with full structure
    const { data: fullCampaign, error: fetchError } = await supabase
      .from('ad_campaigns')
      .select(`
        *,
        ad_sets (
          id,
          name,
          status,
          daily_spend,
          ads (
            id,
            name,
            status,
            review_status,
            daily_spend,
            total_spend,
            ad_creatives(
              id,
              name,
              title,
              type,
              thumbnail_url
            )
          )
        )
      `)
      .eq('id', campaign.id)
      .single();

    if (fetchError) throw fetchError;
    
    console.log('✅ Final campaign structure:', fullCampaign);
    return fullCampaign;
  }

  async updateCampaign(campaignId: string, updates: any) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Verify ownership by checking user's ad_account_ids
    const { data: adAccounts, error: adAccountsError } = await supabase
      .from('ad_accounts')
      .select('id')
      .eq('user_id', user.id);

    if (adAccountsError) throw adAccountsError;
    const adAccountIds = (adAccounts || []).map((a: any) => a.id);
    if (adAccountIds.length === 0) {
      throw new Error('No ad accounts found for this user.');
    }

    const { data: existingCampaign, error: campaignFetchError } = await supabase
      .from('ad_campaigns')
      .select('id, ad_account_id, budget_amount, status')
      .eq('id', campaignId)
      .in('ad_account_id', adAccountIds)
      .single();

    if (campaignFetchError || !existingCampaign) {
      throw new Error('Campaign not found or you do not have permission to update it.');
    }

    const nextBudgetRaw = typeof updates?.budget_amount === 'number' ? updates.budget_amount : existingCampaign.budget_amount;
    const nextBlocks = blocksFromSac(nextBudgetRaw || 0);
    if (nextBlocks <= 0) {
      throw new Error('El presupuesto debe ser al menos 1 bloque de 30 segundos.');
    }
    if (nextBlocks > MAX_BLOCKS_PER_24H_PER_USER) {
      throw new Error(`El máximo por usuario es ${MAX_BLOCKS_PER_24H_PER_USER} bloques cada 24 horas (2 horas).`);
    }

    const nextStatus = typeof updates?.status === 'string' ? updates.status : existingCampaign.status;
    if (nextStatus === 'active') {
      const { data: activeCampaigns, error: activeError } = await supabase
        .from('ad_campaigns')
        .select('id, budget_amount')
        .in('ad_account_id', adAccountIds)
        .eq('status', 'active')
        .neq('id', campaignId);
      if (activeError) throw activeError;

      const activeBlocks = (activeCampaigns || []).reduce((sum: number, c: any) => sum + blocksFromSac(c.budget_amount || 0), 0);
      if (activeBlocks + nextBlocks > MAX_BLOCKS_PER_24H_PER_USER) {
        throw new Error(`Superas el máximo de 2 horas por usuario cada 24h. Te quedan ${Math.max(0, MAX_BLOCKS_PER_24H_PER_USER - activeBlocks)} bloques disponibles.`);
      }

      const { data: account, error: accountError } = await supabase
        .from('ad_accounts')
        .select('sac_balance')
        .eq('id', existingCampaign.ad_account_id)
        .single();
      if (accountError) throw accountError;
      if ((account?.sac_balance || 0) < sacFromBlocks(nextBlocks)) {
        throw new Error('No tienes suficiente saldo de SoArtistic Coins (SAC) para activar con estos bloques. Compra más SAC o reduce los bloques.');
      }
    }

    if (Object.prototype.hasOwnProperty.call(updates, 'budget_amount')) {
      updates.budget_amount = sacFromBlocks(nextBlocks);
    }

    const { data, error } = await supabase
      .from('ad_campaigns')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', campaignId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async updateCampaignStatus(campaignId: string, status: 'active' | 'paused' | 'archived') {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    console.log('🔄 Updating campaign status:', campaignId, 'to', status);

    // Verify ownership by checking user's ad_account_ids
    const { data: adAccounts2, error: adAccountsError2 } = await supabase
      .from('ad_accounts')
      .select('id')
      .eq('user_id', user.id);

    if (adAccountsError2) throw adAccountsError2;
    const adAccountIds2 = (adAccounts2 || []).map((a: any) => a.id);
    if (adAccountIds2.length === 0) {
      throw new Error('No ad accounts found for this user.');
    }

    const { data: existingCampaign, error: campaignFetchError } = await supabase
      .from('ad_campaigns')
      .select('id, ad_account_id, budget_amount')
      .eq('id', campaignId)
      .in('ad_account_id', adAccountIds2)
      .single();

    if (campaignFetchError || !existingCampaign) {
      throw new Error('Campaign not found or you do not have permission to update it.');
    }

    if (status === 'active') {
      const targetBlocks = blocksFromSac(existingCampaign.budget_amount || 0);
      if (targetBlocks <= 0) {
        throw new Error('El consumo debe ser al menos 1 bloque de 30 segundos.');
      }
      if (targetBlocks > MAX_BLOCKS_PER_24H_PER_USER) {
        throw new Error(`El máximo por usuario es ${MAX_BLOCKS_PER_24H_PER_USER} bloques cada 24 horas (2 horas).`);
      }

      const { data: activeCampaigns, error: activeError } = await supabase
        .from('ad_campaigns')
        .select('id, budget_amount')
        .in('ad_account_id', adAccountIds2)
        .eq('status', 'active')
        .neq('id', campaignId);
      if (activeError) throw activeError;

      const activeBlocks = (activeCampaigns || []).reduce((sum: number, c: any) => sum + blocksFromSac(c.budget_amount || 0), 0);
      if (activeBlocks + targetBlocks > MAX_BLOCKS_PER_24H_PER_USER) {
        throw new Error(`Superas el máximo de 2 horas por usuario cada 24h. Te quedan ${Math.max(0, MAX_BLOCKS_PER_24H_PER_USER - activeBlocks)} bloques disponibles.`);
      }

      const { data: account, error: accountError } = await supabase
        .from('ad_accounts')
        .select('sac_balance')
        .eq('id', existingCampaign.ad_account_id)
        .single();
      if (accountError) throw accountError;
      if ((account?.sac_balance || 0) < sacFromBlocks(targetBlocks)) {
        throw new Error('No tienes suficiente saldo de SoArtistic Coins (SAC) para activar esta campaña. Compra más SAC o reduce los bloques.');
      }
    }

    // Update campaign
    const { data: campaign, error: campaignError } = await supabase
      .from('ad_campaigns')
      .update({ 
        status,
        updated_at: new Date().toISOString()
      })
      .eq('id', campaignId)
      .select()
      .single();

    if (campaignError) throw campaignError;

    // Update related ad sets and ads
    if (status === 'active') {
      // Activate ad sets
      await supabase
        .from('ad_sets')
        .update({ status: 'active' })
        .eq('campaign_id', campaignId);

      // Activate ads
      const { data: adSets } = await supabase
        .from('ad_sets')
        .select('id')
        .eq('campaign_id', campaignId);

      if (adSets && adSets.length > 0) {
        const adSetIds = adSets.map(set => set.id);
        await supabase
          .from('ads')
          .update({ status: 'active' })
          .in('ad_set_id', adSetIds);
      }
    } else {
      // Pause/archive ad sets and ads
      const newAdStatus = status === 'archived' ? 'archived' : 'paused';
      
      await supabase
        .from('ad_sets')
        .update({ status: newAdStatus })
        .eq('campaign_id', campaignId);

      const { data: adSets } = await supabase
        .from('ad_sets')
        .select('id')
        .eq('campaign_id', campaignId);

      if (adSets && adSets.length > 0) {
        const adSetIds = adSets.map(set => set.id);
        await supabase
          .from('ads')
          .update({ status: newAdStatus })
          .in('ad_set_id', adSetIds);
      }
    }

    console.log('✅ Updated campaign and related items to status:', status);
    return campaign;
  }

  async deleteCampaign(campaignId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    console.log('🗑️ Deleting campaign:', campaignId);

    const { data: adAccounts, error: adAccountsError } = await supabase
      .from('ad_accounts')
      .select('id')
      .eq('user_id', user.id);

    if (adAccountsError) throw adAccountsError;
    const adAccountIds = (adAccounts || []).map((a: any) => a.id);
    if (adAccountIds.length === 0) {
      throw new Error('Ad account not found or you do not have permission to delete this campaign.');
    }

    const { data: campaignRow, error: campaignFetchError } = await supabase
      .from('ad_campaigns')
      .select('id, status, ad_account_id')
      .eq('id', campaignId)
      .in('ad_account_id', adAccountIds)
      .single();

    if (campaignFetchError || !campaignRow) {
      throw new Error('Campaign not found or you do not have permission to delete this campaign.');
    }

    if (campaignRow.status === 'active') {
      throw new Error('No puedes eliminar una campaña activa. Paúsala o archívala primero.');
    }

    const { error } = await supabase
      .from('ad_campaigns')
      .delete()
      .eq('id', campaignId)
      .eq('ad_account_id', campaignRow.ad_account_id);

    if (error) throw error;
    
    console.log('✅ Campaign deleted successfully');
    return true;
  }

  // Analytics
  async getAnalytics(params: any = {}) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    console.log('📊 Getting analytics with params:', params);

    // 1) Get user's ad account ids
    const { data: adAccounts, error: adAccountsError } = await supabase
      .from('ad_accounts')
      .select('id')
      .eq('user_id', user.id);
    if (adAccountsError) throw adAccountsError;
    const adAccountIds = (adAccounts || []).map((a: any) => a.id);
    if (adAccountIds.length === 0) {
      return { metrics: [], daily_metrics: [], summary: { impressions: 0, clicks: 0, reach: 0, spend: 0, average_ctr: 0, average_cpc: 0, date_range: { start_date: params.start_date, end_date: params.end_date } } };
    }

    // 2) Get campaign ids in those accounts (optionally filter by campaign)
    let campaignsQuery = supabase
      .from('ad_campaigns')
      .select('id, ad_account_id')
      .in('ad_account_id', adAccountIds);
    if (params.campaign_id) {
      campaignsQuery = campaignsQuery.eq('id', params.campaign_id);
    }
    const { data: campaignRows, error: campaignsError } = await campaignsQuery;
    if (campaignsError) throw campaignsError;
    const campaignIds = (campaignRows || []).map((c: any) => c.id);
    if (campaignIds.length === 0) {
      return { metrics: [], daily_metrics: [], summary: { impressions: 0, clicks: 0, reach: 0, spend: 0, average_ctr: 0, average_cpc: 0, date_range: { start_date: params.start_date, end_date: params.end_date } } };
    }

    // 3) Get ad_set ids for those campaigns
    const { data: adSetRows, error: adSetsError } = await supabase
      .from('ad_sets')
      .select('id, campaign_id')
      .in('campaign_id', campaignIds);
    if (adSetsError) throw adSetsError;
    const adSetIds = (adSetRows || []).map((s: any) => s.id);
    if (adSetIds.length === 0) {
      return { metrics: [], daily_metrics: [], summary: { impressions: 0, clicks: 0, reach: 0, spend: 0, average_ctr: 0, average_cpc: 0, date_range: { start_date: params.start_date, end_date: params.end_date } } };
    }

    // 4) Get ad ids for those ad sets
    const { data: adRows, error: adsError } = await supabase
      .from('ads')
      .select('id, ad_set_id')
      .in('ad_set_id', adSetIds);
    if (adsError) throw adsError;
    const adIds = (adRows || []).map((a: any) => a.id);
    if (adIds.length === 0) {
      return { metrics: [], daily_metrics: [], summary: { impressions: 0, clicks: 0, reach: 0, spend: 0, average_ctr: 0, average_cpc: 0, date_range: { start_date: params.start_date, end_date: params.end_date } } };
    }

    // 5) Fetch metrics for those ads
    let metricsQuery = supabase
      .from('ad_metrics')
      .select('*')
      .in('ad_id', adIds);

    // Date filters
    if (params.start_date) metricsQuery = metricsQuery.gte('date', params.start_date);
    if (params.end_date) metricsQuery = metricsQuery.lte('date', params.end_date);

    metricsQuery = metricsQuery.order('date', { ascending: false });

    const { data: metricsData, error } = await metricsQuery;
    if (error) {
      console.error('❌ Analytics error:', error);
      throw error;
    }

    console.log('✅ Raw metrics data:', metricsData?.length || 0, 'records');

    const adSetIdByAdId = new Map<string, string>();
    for (const row of adRows || []) {
      if (row?.id && row?.ad_set_id) adSetIdByAdId.set(row.id, row.ad_set_id);
    }
    const campaignIdByAdSetId = new Map<string, string>();
    for (const row of adSetRows || []) {
      if (row?.id && row?.campaign_id) campaignIdByAdSetId.set(row.id, row.campaign_id);
    }

    const enrichedMetrics = (metricsData || []).map((metric: any) => {
      const adSetId = adSetIdByAdId.get(metric.ad_id);
      const campaignId = adSetId ? campaignIdByAdSetId.get(adSetId) : undefined;
      return {
        ...metric,
        ad_set_id: adSetId,
        campaign_id: campaignId,
      };
    });

    const dailyMap = new Map<string, { date: string; impressions: number; clicks: number; reach: number; spend: number }>();
    for (const metric of enrichedMetrics) {
      const date = String(metric.date || '');
      if (!date) continue;
      const current = dailyMap.get(date) || { date, impressions: 0, clicks: 0, reach: 0, spend: 0 };
      current.impressions += Number(metric.impressions || 0);
      current.clicks += Number(metric.clicks || 0);
      current.reach += Number(metric.reach || 0);
      current.spend += Number(metric.spend || 0);
      dailyMap.set(date, current);
    }
    const dailyMetrics = Array.from(dailyMap.values()).sort((a, b) => a.date.localeCompare(b.date));

    // Aggregate metrics
    const totalMetrics = (dailyMetrics || []).reduce((acc, metric) => ({
      impressions: acc.impressions + (metric.impressions || 0),
      clicks: acc.clicks + (metric.clicks || 0),
      reach: acc.reach + (metric.reach || 0),
      spend: acc.spend + (metric.spend || 0)
    }), { impressions: 0, clicks: 0, reach: 0, spend: 0 });

    const avgCTR = totalMetrics.impressions > 0 ? (totalMetrics.clicks / totalMetrics.impressions) * 100 : 0;
    const avgCPC = totalMetrics.clicks > 0 ? totalMetrics.spend / totalMetrics.clicks : 0;

    const result = {
      metrics: enrichedMetrics,
      daily_metrics: dailyMetrics,
      summary: {
        impressions: Number(totalMetrics.impressions),
        clicks: Number(totalMetrics.clicks),
        reach: Number(totalMetrics.reach),
        spend: Number(totalMetrics.spend.toFixed(2)),
        average_ctr: Number(avgCTR.toFixed(4)),
        average_cpc: Number(avgCPC.toFixed(4)),
        date_range: { start_date: params.start_date, end_date: params.end_date }
      }
    };

    console.log('✅ Analytics result:', result.summary);
    return result;
  }

  async trackAdEvent(params: { ad_id: string; event_type: 'impression' | 'click' }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase.functions.invoke('track-ad-event', {
      body: params,
    });
    if (error) throw error;
    return data;
  }

  // Billing & Stripe Integration
  async createCoinCheckoutSession(params: { package_id: string; return_url: string }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase.functions.invoke('create-coin-checkout-session', {
      body: params,
    });
    if (error) throw error;
    if (!data?.client_secret || typeof data.client_secret !== 'string') {
      throw new Error('Checkout session inválida');
    }
    return { client_secret: data.client_secret as string };
  }

  async getInvoices(accountId?: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Get user's ad account ids
    const { data: adAccounts, error: adAccountsError } = await supabase
      .from('ad_accounts')
      .select('id')
      .eq('user_id', user.id);
    if (adAccountsError) throw adAccountsError;
    const adAccountIds = (adAccounts || []).map((a: any) => a.id);
    if (adAccountIds.length === 0) return [];

    let invoicesQuery = supabase
      .from('ad_invoices')
      .select('*')
      .in('ad_account_id', adAccountIds);

    if (accountId) {
      if (!adAccountIds.includes(accountId)) return [];
      invoicesQuery = invoicesQuery.eq('ad_account_id', accountId);
    }

    invoicesQuery = invoicesQuery.order('created_at', { ascending: false });

    const { data, error } = await invoicesQuery;
    if (error) throw error;
    return data || [];
  }

  async setupStripePayment(accountId: string, paymentMethodId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // This would integrate with Stripe API
    console.log('💳 Setting up Stripe payment for account:', accountId);
    
    const { data, error } = await supabase
      .from('ad_accounts')
      .update({
        stripe_payment_method_id: paymentMethodId,
        updated_at: new Date().toISOString()
      })
      .eq('id', accountId)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Budget Management
  async getBudgetStatus(accountId?: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    let query = supabase
      .from('ad_accounts')
      .select(`
        id,
        account_name,
        sac_balance,
        ad_campaigns(
          id,
          name,
          status,
          budget_amount
        )
      `)
      .eq('user_id', user.id);

    if (accountId) {
      query = query.eq('id', accountId);
    }

    const { data, error } = await query;
    if (error) throw error;
    
    return data?.map(account => ({
      ...account,
      // budget_utilization is no longer directly applicable with SAC balance vs spend limits
      campaigns_active: account.ad_campaigns?.filter(c => c.status === 'active').length || 0,
      total_campaign_budget: account.ad_campaigns?.reduce((sum, c) => sum + (c.budget_amount || 0), 0) || 0
    })) || [];
  }

  // Helper methods
  async pauseCampaign(campaignId: string) {
    return this.updateCampaignStatus(campaignId, 'paused');
  }

  async activateCampaign(campaignId: string) {
    return this.updateCampaignStatus(campaignId, 'active');
  }

  async archiveCampaign(campaignId: string) {
    return this.updateCampaignStatus(campaignId, 'archived');
  }

  // Account setup
  async setupUserAccount(accountData: {
    account_name: string;
    business_name?: string;
    account_type?: 'personal' | 'business';
  }) {
    if (this.setupUserAccountPromise) {
      return this.setupUserAccountPromise;
    }

    this.setupUserAccountPromise = (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const existingAccounts = await this.getAdAccounts();
      if (existingAccounts.length > 0) {
        return existingAccounts[0];
      }

      return this.createAdAccount({
        ...accountData,
        sac_balance: 0
      });
    })();

    try {
      return await this.setupUserAccountPromise;
    } finally {
      this.setupUserAccountPromise = null;
    }
  }

  // Performance Analytics
  async getCampaignPerformance(campaignId: string, days: number = 30) {
    try {
      const { data, error } = await supabase.rpc('get_campaign_performance', {
        p_campaign_id: campaignId,
        p_start_date: new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        p_end_date: new Date().toISOString().split('T')[0]
      });

      if (error) throw error;
      return data[0] || {
        impressions: 0,
        clicks: 0,
        spend: 0,
        ctr: 0,
        cpc: 0,
        reach: 0
      };
    } catch (err) {
      console.error('Error getting campaign performance:', err);
      return null;
    }
  }
}

export const adsApiService = new AdsApiService();
